// SteelOps — Mobile clock-in / clock-out screen
//
// One screen field employees + welders see most. Punches go to the durable
// IndexedDB queue and drain to Supabase `time_cards` on online/focus. The
// queue is keyed by `${employee_id}:${ts}` so retried sends are idempotent
// (server-side upsert on the primary key).

import { useState, useEffect, useMemo } from 'react';
import { useApp } from '@/context/AppContext';
import {
  queuePunch,
  flushQueue,
  subscribeQueueStatus,
  makeSupabaseSender,
} from '@/lib/offlineQueue';
import { supabase } from '@/data/supabase';

export default function MobileClock() {
  const { jobs = [], team = [], userId, userEmail, isOnline, showToast } = useApp();
  const [clockedIn, setClockedIn] = useState(false);
  const [job, setJob]             = useState('');
  const [elapsed, setElapsed]     = useState(0);
  const [pending, setPending]     = useState(0);

  // Find this user's employee_id row, so the punch FK to technicians/employees
  // resolves correctly. If they haven't been added to the team yet, we still
  // accept the punch — employee_id can be null.
  const employeeRow = useMemo(
    () => team.find(t => t.userId === userId) || team.find(t => t.email && t.email === userEmail),
    [team, userId, userEmail]
  );
  const employeeId = employeeRow?.id || null;

  useEffect(() => {
    if (!clockedIn) return undefined;
    const t = setInterval(() => setElapsed(s => s + 1), 1000);
    return () => clearInterval(t);
  }, [clockedIn]);

  useEffect(() => subscribeQueueStatus((s) => setPending(s.pending || 0)), []);

  const fmt = (s) => {
    const h = Math.floor(s / 3600);
    const m = Math.floor((s % 3600) / 60);
    const sec = s % 60;
    return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:${String(sec).padStart(2, '0')}`;
  };

  const punch = async (type) => {
    const punchRow = {
      type,
      employee_id: employeeId,
      job_id:      job ? Number(job) : null,
      ts:          new Date().toISOString(),
    };
    try {
      await queuePunch(punchRow);
    } catch (e) {
      showToast?.(`Punch failed: ${e.message || e}`, false);
      return;
    }

    if (supabase && navigator.onLine) {
      flushQueue(makeSupabaseSender(supabase)).catch(() => {});
    }

    if (type === 'clock_in') {
      setClockedIn(true);
      setElapsed(0);
      showToast?.(isOnline ? 'Clocked in ✓' : 'Clocked in (queued offline)');
    } else {
      setClockedIn(false);
      setElapsed(0);
      showToast?.(isOnline ? 'Clocked out ✓' : 'Clocked out (queued offline)');
    }
  };

  return (
    <div style={{ padding: '20px 16px', maxWidth: 480, margin: '0 auto', display: 'grid', gap: 16 }}>
      <div className="card" style={{ padding: 24, textAlign: 'center' }}>
        <div style={{ fontSize: 13, color: 'var(--t3, #6b7280)', textTransform: 'uppercase', letterSpacing: '.05em' }}>
          {clockedIn ? '🟢 Clocked In' : '⚪ Clocked Out'}
        </div>
        {clockedIn && (
          <div style={{ fontSize: 40, fontWeight: 800, fontVariantNumeric: 'tabular-nums', marginTop: 8 }}>
            {fmt(elapsed)}
          </div>
        )}
      </div>

      {!clockedIn && (
        <div className="card" style={{ padding: 16 }}>
          <label style={{ display: 'block', fontSize: 13, color: 'var(--t3, #6b7280)', marginBottom: 6 }}>Job (optional)</label>
          <select
            value={job}
            onChange={(e) => setJob(e.target.value)}
            style={{ width: '100%', padding: '10px 12px', border: '1px solid var(--b2, #d1d5db)', borderRadius: 6 }}
          >
            <option value="">— Select job —</option>
            {jobs.map(j => (
              <option key={j.id} value={j.id}>
                {j.jobNumber ? `${j.jobNumber} — ${j.projectName || ''}` : (j.projectName || `Job #${j.id}`)}
              </option>
            ))}
          </select>
        </div>
      )}

      <button
        onClick={() => punch(clockedIn ? 'clock_out' : 'clock_in')}
        style={{
          padding: '20px 0',
          borderRadius: 14,
          border: 'none',
          fontSize: 20,
          fontWeight: 800,
          color: 'white',
          background: clockedIn
            ? 'linear-gradient(135deg, #ef4444, #b91c1c)'
            : 'linear-gradient(135deg, #14b8a6, #06b6d4)',
          cursor: 'pointer',
        }}
      >
        {clockedIn ? '🛑 Clock Out' : '▶ Clock In'}
      </button>

      <div style={{ textAlign: 'center', fontSize: 12, color: 'var(--t3, #6b7280)' }}>
        Punches are saved locally and sync to the cloud automatically when online.
        {pending > 0 && (
          <> · <strong>{pending}</strong> queued</>
        )}
        {!supabase && (
          <div style={{ marginTop: 4, color: 'var(--amber)' }}>
            Cloud sync disabled in local-mode preview. Punches stay on this device.
          </div>
        )}
      </div>
    </div>
  );
}
